import React, { useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Button from "../../components/Button/Button";
import DocumentCard from "../../components/DocumentCard/DocumentCard";
import Typography from "../../components/Typography/Typography";
import ScrollContainer from "react-indiana-drag-scroll";

const DocumentPagination = ({ response, loading }) => {
  // Add safety check for response
  const safeResponse = response || {};

  const allDocuments = [
    ...(safeResponse.userDocument || safeResponse.documents || []),
    ...(safeResponse.userImages || safeResponse.images || []),
    ...(safeResponse.userUrls || safeResponse.urls || []),
    ...(safeResponse.semanticDocument ||
      safeResponse.semantic_scholar_results ||
      []),
  ];

  // State to track current page
  const [currentPage, setCurrentPage] = useState(0);

  // Number of documents per page
  const DOCS_PER_PAGE = 3;

  // Check for window to handle server-side rendering
  const isMobile =
    typeof window !== "undefined" ? window.innerWidth <= 768 : false;

  const getCurrentPageDocuments = () => {
    const start = currentPage * DOCS_PER_PAGE;

    const documentsToUse =
      Array.isArray(allDocuments) && allDocuments.length > 0
        ? allDocuments
        : Array.isArray(safeResponse) && safeResponse.length > 0
        ? safeResponse
        : [];

    // Return documents for current page
    return isMobile
      ? documentsToUse
      : documentsToUse.slice(start, start + DOCS_PER_PAGE);
  };

  // Handle next page
  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  // Handle previous page
  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  const totalPages = Math.max(
    1,
    Math.ceil(allDocuments.length / DOCS_PER_PAGE)
  );

  // Check if we've loaded data but found no documents
  const isLoaded = response !== undefined && !loading;
  const noDocumentsFound = isLoaded && allDocuments.length === 0;

  return (
    <div className="md:w-1/3 h-fit md:sticky md:top-0 w-full flex flex-col md:py-6 py-10 pb-0 justify-between md:border-b-2 md:border-gray-150">
      <div className="flex flex-col gap-6">
        <div className="px-6">
          <Typography size="20" desktopSize="32" weight="sb" family="serif">
            Fonti
          </Typography>
        </div>
        <div className="w-full">
          <ScrollContainer horizontal>
            <div className="px-6 w-fit md:w-auto flex md:flex-col gap-6 overflow-x-auto mb-3">
              {loading ? (
                // Show skeleton loaders only when loading
                <>
                  {[...Array(3)].map((_, i) => (
                    <div
                      key={i}
                      className="w-full aspect-[4/3] rounded-[8px] md:rounded-[12px] bg-gray-100 animate-pulse"
                    />
                  ))}
                </>
              ) : noDocumentsFound ? (
                // Show message when no documents found
                <div className="flex flex-col justify-start gap-2">
                  <Typography size="16" color="gray-600">
                    Nessuna fonte trovata per questa ricerca.
                  </Typography>
                  <div className="md:block hidden w-full md:pe-20 lg:pe-36">
                    <img
                      src="/assets/images/illustrations/no_docs_found.png"
                      alt="no docs found"
                      className="w-full"
                    />
                  </div>
                </div>
              ) : (
                // Show documents when available
                getCurrentPageDocuments().map((doc, index) => {

                  // Skip if document is null or undefined
                  if (!doc) return null;

                  const number = currentPage * DOCS_PER_PAGE + index + 1;
                  const siteUrl = doc.openAccessPdf?.url || doc.siteUrl;

                  // Handle semantic scholar documents
                  if (!doc.CreationDate) {
                    return (
                      <DocumentCard
                        key={doc.paperId || `doc-${number}`}
                        isRatio={false}
                        showNumber={true}
                        number={number}
                        date=""
                        year={doc.year || ""}
                        journalName={doc.journal?.name || ""}
                        id={doc.paperId}
                        title={doc.title || "Untitled"}
                        hasPdf={!!doc.openAccessPdf?.url}
                        isScholar={true}
                        description={doc.abstract || ""}
                        siteUrl={siteUrl}
                        imageUrl={doc.thumbnail_url}
                      />
                    );
                  }

                  // Estrai l'ID corretto in base al tipo di documento
                  let documentId;
                  if (doc.sk) {
                    if (doc.sk.startsWith("document#")) {
                      // Per documenti PDF, estrai il percorso completo dopo "document#"
                      documentId = doc.sk.substring(9); // Rimuovi "document#" dall'inizio
                    } else if (doc.sk.startsWith("image#")) {
                      // Per immagini, estrai il percorso completo dopo "image#"
                      documentId = doc.sk.substring(6); // Rimuovi "image#" dall'inizio
                    } else if (doc.sk.startsWith("url#")) {
                      // Per URL, estrai solo il dominio base (senza parametri di query)
                      const fullUrl = doc.sk.substring(4); // Rimuovi "url#" dall'inizio
                      try {
                        const urlObj = new URL(fullUrl);
                        documentId = urlObj.hostname + urlObj.pathname;
                      } catch (e) {
                        // Se l'URL non è valido, usa solo la parte prima del primo '?'
                        documentId = fullUrl.split("?")[0];
                      }
                    } else {
                      // Fallback all'id esistente o al number
                      documentId = doc.id || number;
                    }
                  } else {
                    // Fallback all'id esistente o al number
                    documentId = doc.id || number;
                  }

                  return (
                    <DocumentCard
                      key={doc.id || number}
                      isRatio={false}
                      showNumber={true}
                      number={number}
                      id={documentId}
                      title={doc.title || "Untitled"}
                      isScholar={!!doc.authors}
                      description={
                        doc.abstract ||
                        doc.description ||
                        "Descrizione non disponibile"
                      }
                      imageUrl={
                        doc.thumbnail_url ||
                        doc.image_url ||
                        doc.thumbnail ||
                        doc.image ||
                        "path_to_default_image.jpg"
                      }
                      placeholder={
                        !doc.thumbnail_url &&
                        !doc.image_url &&
                        !doc.thumbnail &&
                        !doc.image
                      }
                      siteUrl={siteUrl}
                    />
                  );
                })
              )}
            </div>
          </ScrollContainer>
        </div>
      </div>
      {totalPages > 1 && (
        <div className="hidden md:flex gap-6 items-center px-6">
          <div className="flex gap-3 items-center">
            <Button
              disabled={currentPage === 0}
              type="bw"
              icon={<ArrowLeft size={16} />}
              size="sm"
              onClick={() => {
                handlePrevPage();
              }}
            />
            <Button
              disabled={currentPage === totalPages - 1}
              type="bw"
              icon={<ArrowRight size={16} />}
              size="sm"
              onClick={() => {
                handleNextPage();
              }}
            />
          </div>
          <Typography size="16" weight="md">
            {currentPage + 1}{" "}
            <Typography color="gray-200">/ {totalPages}</Typography>
          </Typography>
        </div>
      )}
    </div>
  );
};

export default DocumentPagination;
